import { getApiUrl } from '../config/api.config.jsx';

/**
 * Storage routes use JWT (Bearer). Cookies not required for these calls.
 * Token is read from localStorage key `token` (set on login from API payload).
 */
const STORAGE_FETCH_CREDENTIALS = 'omit';

// API-only storage client. Legacy in-browser AWS access has been removed.

/**
 * API envelope: { status: "success", data: {...} } or { status: "error", message }.
 * Returns inner `data` when present so callers see uploadUrl, key, objects, url, etc.
 */
function unwrapPayload(json) {
  if (json && typeof json === 'object' && json.data !== undefined) {
    return json.data;
  }
  return json;
}

async function readResponseJson(res) {
  const text = await res.text();
  if (!text) return {};
  const contentType = res.headers.get('content-type') || '';
  const looksJson =
    contentType.includes('application/json') || contentType.includes('+json');
  try {
    return JSON.parse(text);
  } catch {
    if (!res.ok) {
      const snippet = text.replace(/\s+/g, ' ').trim().slice(0, 240);
      throw new Error(
        snippet || `Non-JSON error body (${contentType || 'unknown content-type'})`
      );
    }
    if (looksJson) {
      throw new Error('Invalid JSON from storage API');
    }
    throw new Error(`Expected JSON, received ${contentType || 'unknown type'}`);
  }
}

function storagePath(suffix) {
  let prefix = String(import.meta.env.VITE_STORAGE_API_PREFIX ?? 'storage').replace(/^\/+|\/+$/g, '');
  if (!prefix) prefix = 'storage';
  const tail = suffix.replace(/^\/+/, '');
  return `/${prefix}/${tail}`;
}

function storageJwtHeaders() {
  if (typeof localStorage === 'undefined') {
    throw new Error('Storage API: localStorage unavailable');
  }
  const token = localStorage.getItem('token');
  if (!token || !String(token).trim()) {
    throw new Error('Storage API: no JWT (localStorage "token"); sign in again.');
  }
  return {
    Authorization: `Bearer ${String(token).trim()}`,
  };
}

async function storageFetch(suffix, options = {}) {
  const base = getApiUrl().replace(/\/$/, '');
  const rel = storagePath(suffix);
  const url = `${base}${rel}`;
  const { body, method = 'GET', headers: extraHeaders } = options;

  const headers = {
    ...extraHeaders,
    ...storageJwtHeaders(),
  };
  if (body !== undefined && body !== null && typeof body === 'object' && !(body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  const init = {
    method,
    credentials: STORAGE_FETCH_CREDENTIALS,
    headers,
  };

  if (body instanceof FormData) {
    delete init.headers['Content-Type'];
    init.body = body;
  } else if (body !== undefined && body !== null) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  const res = await fetch(url, init);
  let json;
  try {
    json = await readResponseJson(res);
  } catch (e) {
    if (!res.ok) {
      throw new Error(`Storage API ${res.status}: ${e.message || res.statusText}`);
    }
    throw e;
  }
  if (!res.ok) {
    const msg = json?.message || json?.error || res.statusText || 'Storage API request failed';
    const text = typeof msg === 'string' ? msg : JSON.stringify(msg);
    throw new Error(`Storage API ${res.status}: ${text}`);
  }
  if (json && typeof json === 'object' && json.status === 'error') {
    throw new Error(json.message || 'Storage API error');
  }
  return unwrapPayload(json);
}

const DOCUMENT_TYPE_TO_STEP_ID = {
  nda: 1,
  meeting: 2,
  partnership: 2,
  shariyah: 5,
  legal: 6,
  productDevelopment: 4,
  training: 4,
  techSetup: 7,
  launch: 8,
  businessDocumentation: 9,
};

function resolvePresignStepId(documentType) {
  if (documentType === null || documentType === undefined || documentType === '') {
    throw new Error('Storage API presign-upload: stepId is required (pass documentType / step key)');
  }
  if (typeof documentType === 'number' && !Number.isNaN(documentType)) {
    return documentType;
  }
  if (typeof documentType === 'string' && /^\d+$/.test(documentType.trim())) {
    return parseInt(documentType.trim(), 10);
  }
  if (typeof documentType === 'string') {
    const id = DOCUMENT_TYPE_TO_STEP_ID[documentType];
    if (id != null) return id;
  }
  throw new Error(
    `Storage API presign-upload: unknown documentType / stepKey "${String(documentType)}"`
  );
}

/**
 * HalalCredApi storage (JWT Bearer). Paths under {API}/api/storage/...
 *
 * POST /storage/presign-upload — body { stepId, fileName, contentType? }
 * GET /storage/presign-download?s3Key=…  (or POST + JSON { s3Key } — we use GET per API doc)
 * GET /storage/list?prefix=&maxKeys=
 * DELETE /storage/object — body { s3Key }
 */
export class StorageBackendClient {
  constructor() {
    this.isInitialized = false;
  }

  async initialize() {
    this.isInitialized = true;
    return true;
  }

  isReady() {
    return this.isInitialized;
  }

  async ensureInitialized() {
    if (!this.isReady()) {
      const ok = await this.initialize();
      if (!ok) {
        throw new Error('Failed to initialize storage API client');
      }
    }
  }

  async uploadFile(file, partnerId, documentType) {
    await this.ensureInitialized();
    const stepId = resolvePresignStepId(documentType);
    const presign = await storageFetch('presign-upload', {
      method: 'POST',
      body: {
        stepId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      },
    });

    const key = presign.key || presign.s3Key;
    const uploadUrl = presign.uploadUrl || presign.url;
    if (!key || !uploadUrl) {
      throw new Error('Storage API: presign-upload response missing key or uploadUrl');
    }

    const serverHeaders = presign.headers && typeof presign.headers === 'object' ? presign.headers : {};
    const putHeaders = { ...serverHeaders };
    if (!putHeaders['Content-Type'] && !putHeaders['content-type']) {
      putHeaders['Content-Type'] = file.type || 'application/octet-stream';
    }

    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: putHeaders,
    });

    if (!putRes.ok) {
      const errText = await putRes.text().catch(() => '');
      throw new Error(errText || `Upload to storage failed (${putRes.status})`);
    }

    const etag =
      putRes.headers.get('ETag')?.replace(/"/g, '') || `${Date.now()}`;
    const s3Url = presign.s3Url ?? '';

    return {
      id: etag,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString(),
      status: 'uploaded',
      s3Key: key,
      s3Url,
      s3Bucket: presign.bucket,
      documentType,
      partnerId,
    };
  }

  async deleteFile(s3Key) {
    await this.ensureInitialized();
    await storageFetch('object', {
      method: 'DELETE',
      body: { s3Key },
    });
    return { success: true };
  }

  /**
   * Presign download. Optional `hint` can be `{ stepId }` if your API scopes access by step.
   * A numeric second argument (legacy) is ignored (TTL is server-controlled).
   *
   * Tries GET `presign-download?s3Key=…` first, then POST JSON `{ s3Key, stepId? }` if GET returns 403/405
   * (some deployments only authorize one shape).
   */
  async getSignedUrl(s3Key, hint) {
    await this.ensureInitialized();
    const opts =
      hint != null && typeof hint === 'object' && !Array.isArray(hint) ? hint : {};
    const stepId = opts.stepId;

    const qs = new URLSearchParams({ s3Key });
    if (stepId != null && stepId !== '') qs.set('stepId', String(stepId));

    const postBody = { s3Key };
    if (stepId != null && stepId !== '') postBody.stepId = stepId;

    const unwrapUrl = (data) => {
      const url = data.url || data.downloadUrl || data.signedUrl;
      if (!url) {
        throw new Error(
          'Storage API: presign-download response missing url (expected data.url from success envelope)'
        );
      }
      return url;
    };

    try {
      const data = await storageFetch(`presign-download?${qs.toString()}`, {
        method: 'GET',
      });
      return unwrapUrl(data);
    } catch (e) {
      const msg = String(e?.message || e || '');
      if (!/\b(403|405)\b/.test(msg)) throw e;
      try {
        const data = await storageFetch('presign-download', {
          method: 'POST',
          body: postBody,
        });
        return unwrapUrl(data);
      } catch (e2) {
        throw new Error(
          `${String(e2?.message || e2)} Original GET error: ${msg}`
        );
      }
    }
  }

  normalizeListItem(row) {
    if (!row) return null;
    const key = row.key || row.Key;
    if (!key) return null;
    return {
      key,
      name: row.name || (key && key.split('/').pop()),
      size: row.size ?? row.Size,
      lastModified: row.lastModified || row.LastModified,
      etag: row.etag || row.ETag,
    };
  }

  async listFiles(prefix = '', maxKeys = 100) {
    await this.ensureInitialized();
    const capped = Math.min(Math.max(1, Number(maxKeys) || 500), 1000);
    const q = new URLSearchParams({ prefix, maxKeys: String(capped) });
    const data = await storageFetch(`list?${q.toString()}`, { method: 'GET' });

    const raw =
      data.objects ||
      data.files ||
      data.contents ||
      data.Contents ||
      data.items ||
      (Array.isArray(data) ? data : []);

    const list = Array.isArray(raw) ? raw : [];
    return list.map((row) => this.normalizeListItem(row)).filter(Boolean);
  }

  async uploadResourceFile(file) {
    return this.uploadGenericResource(file, 'sharia-templates');
  }

  async uploadGenericResource(file, resourceType = 'sharia-templates', folder = null) {
    await this.ensureInitialized();
    const genericStepId =
      import.meta.env.VITE_STORAGE_RESOURCE_STEP_ID != null &&
      String(import.meta.env.VITE_STORAGE_RESOURCE_STEP_ID).trim() !== ''
        ? import.meta.env.VITE_STORAGE_RESOURCE_STEP_ID
        : `resource:${resourceType}${folder ? `/${folder}` : ''}`;

    const presign = await storageFetch('presign-upload', {
      method: 'POST',
      body: {
        stepId: genericStepId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      },
    });

    const key = presign.key || presign.s3Key;
    const uploadUrl = presign.uploadUrl || presign.url;
    if (!key || !uploadUrl) {
      throw new Error('Storage API: presign-upload response missing key or uploadUrl');
    }

    const serverHeaders = presign.headers && typeof presign.headers === 'object' ? presign.headers : {};
    const putHeaders = { ...serverHeaders };
    if (!putHeaders['Content-Type'] && !putHeaders['content-type']) {
      putHeaders['Content-Type'] = file.type || 'application/octet-stream';
    }

    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: putHeaders,
    });

    if (!putRes.ok) {
      const errText = await putRes.text().catch(() => '');
      throw new Error(errText || `Upload to storage failed (${putRes.status})`);
    }

    const etag =
      putRes.headers.get('ETag')?.replace(/"/g, '') || `${Date.now()}`;
    const s3Url = presign.s3Url ?? '';

    return {
      id: etag,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString(),
      status: 'uploaded',
      s3Key: key,
      s3Url,
      s3Bucket: presign.bucket,
    };
  }
}
